<template>
  <div class="reports-view details client-details-view mt-2">
    <Header class="px-5" ref="header" :title="$route.meta.title" :buttons="[]">
      <v-btn outlined color="primary" class="ml-4" @click="exportData(true)">
        Eksportuj do xlsx
      </v-btn>
      <v-btn outlined color="primary" class="ml-4" @click="exportData(false)">
        Eksportuj do csv
      </v-btn>
    </Header>

    <FiltersBar :table-name="tableName" search-bar date-range-picker show-filters-button table-filters />

    <div class="px-4">
      <v-row>
        <v-col cols="12" md="8">
          <div class="detail-section__wrapper">
            <div class="detail-section__header">
              Zamówienia firm
            </div>
            <div class="detail-section__content">
              <v-data-table
                class="report-viewport"
                :class="{ 'filters-open': showFilters }"
                :item-class="() => 'table__row'"
                :headers="headers"
                :items="clients"
                :items-per-page="-1"
                fixed-header
                hide-default-footer
                show-expand
                single-expand
                item-key="id"
                :loading="isProcessing"
                :expanded.sync="expandedClients"
                @click:row="(item, slot) => expandRow(slot)"
              >
                <template #expanded-item="{ headers: tableHeaders, item }">
                  <td :colspan="tableHeaders.length">
                    <v-data-table
                      class="expanded-table br-0"
                      :headers="subtableHeaders"
                      :items="item.addressData"
                      :items-per-page="-1"
                      fixed-header
                      hide-default-footer
                    >
                      <template #[`item.totalGrossValue`]="{item: expandableItem}">
                        {{ formatValue(expandableItem.totalGrossValue, 'price') }}
                      </template>
                      <template #[`item.details`]="{item: expandableItem}">
                        <v-btn text color="primary" small @click="openAddressDetails(expandableItem)">
                          Wyświetl zlecenia
                        </v-btn>
                      </template>
                    </v-data-table>
                  </td>
                </template>
                <template #[`item.data-table-expand`]="{ item }">
                  <td>
                    <v-btn v-if="item.addressData && item.addressData.length" icon small>
                      <v-icon>mdi-chevron-down</v-icon>
                    </v-btn>
                  </td>
                </template>
                <template #[`item.totalGrossValue`]="{item}">
                  {{ formatValue(item.totalGrossValue, 'price') }}
                </template>
                <template #[`item.trend`]="{item}">
                  <v-sparkline
                    :value="getTrendValues(item.monthData)"
                    line-width="2"
                    smooth
                    :padding="12"
                    stroke-linecap="round"
                    type="trend"
                    auto-line-width
                    auto-draw
                  />
                </template>
              </v-data-table>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="detail-section__wrapper mb-5">
            <div class="detail-section__header">
              Zlecenia z podziałem na miesiące
            </div>
            <div class="detail-section__content">
              <v-data-table
                v-if="expandedClients.length"
                :item-class="() => 'table__row'"
                :headers="monthParameters"
                :items="monthsData"
                :items-per-page="-1"
                fixed-header
                hide-default-footer
              >
                <template #[`item.totalGrossValue`]="{item}">
                  {{ formatValue(item.totalGrossValue, 'price') }}
                </template>
              </v-data-table>
              <div v-else class="pa-4">
                Brak danych do wyświetlenia
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
/* global dhx */
import Header from '../../components/Layout/Header'
import FiltersBar from '../../components/Filters/FiltersBar'
import { mapState, mapActions } from 'vuex'
import { formatValue, months } from '../../utils/typesEnum'
import DateTime from 'luxon/src/datetime'
import pivotLocale from '../../const/pivotLocale'

const formatPriceCell = (text, _, col) => {
  if (col.method === 'count') return text
  return text ? Number(text).toFixed(2) + ' zł' : '-'
}

const monthParameters = [
  { text: 'Rok', value: 'year' },
  { text: 'Miesiąc', value: 'monthName' },
  { text: 'Zlecenia', value: 'ordersCount' },
  { text: 'Kwota', value: 'totalGrossValue' }
]

const subtableHeaders = [
  { text: 'Adres', value: 'name' },
  { text: 'Liczba zleceń', value: 'ordersCount' },
  { text: 'Kwota', value: 'totalGrossValue' },
  { text: '', value: 'details' }
]

export default {
  components: {
    Header,
    FiltersBar
  },
  data: () => ({
    tableName: 'clientsReports',
    expandedClients: [],
    monthParameters,
    subtableHeaders,
    configured: false,
    pivotEntity: {}
  }),
  computed: {
    ...mapState({
      isProcessing: state => state.clientsReports.isProcessing,
      clients: state => state.clientsReports.items,
      dateRange: state => state.clientsReports.dateRange,
      showFilters: state => state.clientsReports.showFilters
    }),
    headers() {
      return this.$store.getters['tables/getTableConfig'](this.tableName)?.parameters || []
    },
    emptyMonthsData() {
      const [start, end] = this.dateRange
      const diffInMonths = Math.ceil(
        DateTime.fromISO(end || start)
          .diff(DateTime.fromISO(start), 'months')
          .toObject().months
      )
      return new Array(diffInMonths).fill({}).map((_, index) => {
        const date = new Date(start)
        const month = date.getMonth() + index + 1 // one for rails month order (january is 1)
        const monthIndex = month % 12 || 12

        const yearExpander = Math.floor((month - 1) / 12)
        const year = month > 12 ? date.getFullYear() + yearExpander : date.getFullYear()

        return {
          year,
          monthName: months[monthIndex - 1],
          month: monthIndex,
          ordersCount: 0,
          totalGrossValue: 0
        }
      })
    },
    monthsData() {
      const [data] = this.expandedClients
      if (!data) return []
      const { monthData } = data
      return this.emptyMonthsData.map(emptyMonth => {
        const sample = monthData.find(month => month.month === emptyMonth.month && month.year === emptyMonth.year)
        const { monthName, year } = emptyMonth
        const returnValue = sample ? { ...sample, monthName, year } : emptyMonth
        return returnValue
      })
    },
    parsedCustomReportData() {
      if (!this.clients || !Array.isArray(this.clients)) {
        return []
      }

      // Transform data from nested structure to flat structure
      const flatData = []

      this.clients.forEach(client => {
        const clientId = client.id
        const clientName = client.name || ''
        const clientOrdersCount = client.ordersCount || 0
        const clientTotalGrossValue = client.totalGrossValue || 0

        const addressData = client.addressData

        // If the client has addresses, create a row for each address
        if (addressData && Array.isArray(addressData) && addressData.length > 0) {
          addressData.forEach(address => {
            const totalGrossValue = (Number(address.totalGrossValue) || 0) / 100

            flatData.push({
              clientId,
              clientName,
              addressId: address.addressId,
              addressName: address.name || '',
              ordersCount: address.ordersCount || 0,
              totalGrossValue
            })
          })
        } else {
          // If the client has no addresses, create one row with client data
          flatData.push({
            clientId,
            clientName,
            addressId: null,
            addressName: 'Brak adresu',
            ordersCount: clientOrdersCount,
            totalGrossValue: clientTotalGrossValue
          })
        }
      })

      return flatData
    }
  },
  created() {
    const pivot = document.createElement('script')
    pivot.setAttribute('src', 'pivot-1.5.6.min.js')
    document.head.appendChild(pivot)
  },
  mounted() {
    this.setTableConfig({ tableName: this.tableName }).then(() => {
      dhx.i18n.setLocale('pivot', pivotLocale)
      const fields = {
        rows: ['clientName', 'addressName'],
        columns: [],
        values: [
          { id: 'ordersCount', method: 'count' },
          { id: 'totalGrossValue', method: 'sum' }
        ]
      }

      const fieldList = [
        { id: 'clientName', label: 'Klient' },
        { id: 'clientId', label: 'ID Klienta' },
        { id: 'addressName', label: 'Lokalizacja' },
        { id: 'addressId', label: 'ID Adresu' },
        {
          id: 'ordersCount',
          label: 'Liczba zamówień',
          cellTemplate: text => text || '0'
        },
        {
          id: 'totalGrossValue',
          label: 'Wartość brutto',
          cellTemplate: formatPriceCell
        }
      ]

      this.pivotEntity = new dhx.Pivot('pivot', {
        data: this.parsedCustomReportData,
        fields,
        fieldList,
        layout: {
          columnsWidth: 'auto',
          rowsHeadersWidth: 'auto',
          footer: true
        }
      })

      this.configured = true
    })
  },
  beforeDestroy() {
    if (this.pivot) this.pivot.destructor()
  },
  watch: {
    parsedCustomReportData(data) {
      if (this.configured) this.pivotEntity.setData(data)
    }
  },
  methods: {
    ...mapActions({
      fetchReports: 'clientsReports/getItems',
      setTableConfig: 'tables/setTableConfig',
      setClientOrdersFilters: 'tables/setTableFilters',
      setClientOrdersDateRange: 'clientOrders/setTableDate'
    }),
    formatValue,
    expandRow(slot) {
      if (slot.item.addressData?.length) slot.expand(!slot.isExpanded)
    },
    getTrendValues(samples) {
      return this.emptyMonthsData.map(monthData => {
        const sample = samples.find(samp => samp.month === monthData.month && samp.year === monthData.year)
        return sample?.ordersCount || 0
      })
    },
    openAddressDetails(address) {
      this.setTableConfig({ tableName: 'clientOrders', disableFetch: true })
      this.setClientOrdersDateRange({ date: [...this.dateRange] })
      this.setClientOrdersFilters({
        filters: [{ name: 'addressIds', filterBy: [{ name: address.name, value: address.addressId }] }],
        tableName: 'clientOrders'
      })
      this.$router.push({ name: 'singleClient', params: { id: address.clientId } })
    },
    exportData(isXlsx) {
      const name = `custom report - ${new Date().toISOString()}`
      if (isXlsx) {
        this.pivotEntity.export.xlsx({
          name,
          url: '//export.dhtmlx.com/excel'
        })
      } else {
        this.pivotEntity.export.csv({
          name, // pivot data will be exported to a CSV file named "pivot_data"
          flat: true, // pivot data will be presented as a flat structure
          rowDelimiter: '\r\n', // the tab delimiter will be used to separate rows
          columnDelimiter: ';' // the semicolon delimiter will be used to separate col
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.report-viewport::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 294px);
}
.report-viewport.filters-open::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 380px);
}
</style>
